import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchNewbuildingClusters,
  openNewbuildingPolygon,
  setActiveNewbuildingPolygon,
} from 'shared/map-search/actions/newbuildingPolygon';
import { useApplicationContext } from 'shared/map-search/containers/ApplicationContext';
import { useNewbuildingPolygonsContext } from 'shared/map-search/containers/Map/MapLayers/NewbuildingsPolygons/context';
import { MAP_SET_CENTER_OPTIONS } from 'shared/map-search/containers/Map/constants';
import { useMapContext } from 'shared/map-search/containers/Map/context';
import { toListingUrl } from 'shared/map-search/mappers/queryString';
import { selectActiveNewbuildingPolygon } from 'shared/map-search/selectors/newbuildingPolygons';
import { selectQueryString } from 'shared/map-search/selectors/queryString';
import { IActiveNewbuildingPolygon } from 'shared/map-search/types/map/newbuildingPolygon';
import { IApplicationState, TThunkDispatch } from 'shared/map-search/types/redux';
import { getNewbuildingQuery } from 'shared/map-search/utils/queryString';
import { removeNewbuildingsFromUrl } from 'shared/map-search/utils/removeNewbuildingsFromUrl';

import { trackClickPolygon } from './tracking';

export const PolygonClickHandlers: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { newbuildingsPolygonsManager } = useNewbuildingPolygonsContext();
  const { map } = useMapContext();
  const { custom } = useApplicationContext();

  const queryString = useSelector<IApplicationState, string>(selectQueryString);
  const nonGeoQueryString = useSelector<IApplicationState, string>(state => state.nonGeoQueryString);
  const activePolygon = useSelector<IApplicationState, IActiveNewbuildingPolygon>(selectActiveNewbuildingPolygon);
  const deviceType = useDeviceType();

  const handleClick = React.useCallback(
    async (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const coords = event.get<YMaps.TCoord>('coords');

      const newbuilding = {
        id: Number(objectId),
      };

      if (deviceType === 'desktop' && activePolygon.newbuildingId !== newbuilding.id) {
        map.setCenter(coords, undefined, MAP_SET_CENTER_OPTIONS);
        dispatch(openNewbuildingPolygon(newbuilding));
        trackClickPolygon(queryString, deviceType, newbuilding.id);
      }

      if (deviceType !== 'desktop') {
        dispatch(
          setActiveNewbuildingPolygon({
            prevActiveGeoHashIds: activePolygon.geohashIds,
          }),
        );
        await dispatch(fetchNewbuildingClusters(newbuilding.id, false));

        trackClickPolygon(queryString, deviceType, newbuilding.id);

        const query = getNewbuildingQuery({
          qs: nonGeoQueryString,
          newbuilding,
          selectAllHouseIds: true,
          preselectedFromDeveloper: true,
        });

        const urlFromQs = `${toListingUrl(query)}&origin=map&pinType=Polygon`;
        const urlWithoutNewbuildings = removeNewbuildingsFromUrl(urlFromQs);
        const currentSubdomain = custom.subdomain;

        window.open(
          `//${window.location.hostname.replace(`${currentSubdomain}.`, 'www.')}${urlWithoutNewbuildings}`,
          '_blank',
        );
      }
    },
    [queryString, nonGeoQueryString, deviceType, activePolygon, map, dispatch, custom.subdomain],
  );

  React.useEffect(() => {
    newbuildingsPolygonsManager.objectManager.objects.events.add('click', handleClick);

    return () => {
      newbuildingsPolygonsManager.objectManager.objects.events.remove('click', handleClick);
    };
  }, [handleClick, newbuildingsPolygonsManager]);

  return null;
};
